import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Truck, Shield, RefreshCw, ChevronDown, AlertCircle, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatPrice } from "../i18n/translations";
import ProductCard from "../components/ProductCard";
import SEO, { buildBreadcrumbSchema, buildProductSchema } from "../components/SEO";

const ProductPage = () => {
  const { slug } = useParams();
  const { t, language, currency, addToCart, toggleWishlist, wishlist, products } = useApp();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [openFaq, setOpenFaq] = useState(null);
  const [openAccordion, setOpenAccordion] = useState("details");
  const [showSticky, setShowSticky] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySent, setNotifySent] = useState(false);
  const isDE = language === "de";

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    window.scrollTo(0, 0);
    const found = products.find((p) => p.slug === slug || p.slug_de === slug || p.id === slug || p.ref === slug);
    if (found) {
      setProduct(found);
      setReviews(found.reviews || []);
      // SEO meta updates
      const meta = isDE ? found.meta_de : found.meta_en;
      const title = isDE ? found.name_de : found.name_en;
      if (meta) {
        document.title = `${title} | LUXERA KaLiMa`;
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute("content", meta);
      }
    }
  }, [slug, isDE, products]);

  useEffect(() => {
    const h = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const notifyRestock = (e) => {
    e.preventDefault();
    setNotifySent(true);
    setNotifyEmail("");
  };

  if (!product) return <div className="min-h-screen flex items-center justify-center"><div className="font-display text-3xl">Loading...</div></div>;

  const name = isDE ? product.name_de : product.name_en;
  const tagline = isDE ? product.tagline_de : product.tagline_en;
  const desc = isDE ? product.description_de : product.description_en;
  const bullets = isDE ? product.bullets_de : product.bullets_en;
  const badges = isDE ? product.badges_de : product.badges_en;
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const inWish = wishlist.includes(product.id);
  const oos = !product.in_stock;
  const related = products.filter((p) => p.id !== product.id && p.in_stock).slice(0, 4);

  const faqs = isDE ? [
    { q: "Ist der Schmuck wirklich wasserfest?", a: "Ja, zu 100%. Du kannst duschen, schwimmen und ihn jeden Tag tragen — ohne Sorge." },
    { q: "Läuft er an?", a: "Nein. Unsere 18K Goldbeschichtung (PVD) auf Edelstahl ist anlaufgeschützt und langlebig." },
    { q: "Ist er für sensible Haut geeignet?", a: "Ja. Nickelfrei dank Titan-Zwischenschicht, hypoallergen und für empfindliche Haut geeignet." },
    { q: "Wie lange dauert der Versand?", a: "Kostenloser Versand in Deutschland. Lieferung in 3-5 Werktagen." },
  ] : [
    { q: "Is this jewelry truly waterproof?", a: "Yes — 100%. You can shower, swim and wear it every day without worry." },
    { q: "Will it tarnish?", a: "No. Our 18K PVD gold plating on stainless steel is tarnish-free and built to last." },
    { q: "Is it safe for sensitive skin?", a: "Yes. Nickel-free thanks to a titanium underlayer. Hypoallergenic and ideal for sensitive skin." },
    { q: "How long is shipping?", a: "Free shipping in Germany. Delivery in 3-5 business days. Worldwide shipping available." },
  ];

  return (
    <main className="pt-8 pb-24" data-testid="product-page">
      <SEO
        title={isDE ? (product.meta_de || product.name_de) : (product.meta_en || product.name_en)}
        description={isDE
          ? (product.meta_de || product.tagline_de || "").slice(0, 160)
          : (product.meta_en || product.tagline_en || "").slice(0, 160)}
        canonical={`/product/${product.slug}`}
        ogImage={product.images[0]}
        hreflangs={{
          en: `/product/${product.slug}`,
          de: `/product/${product.slug_de || product.slug}`,
        }}
        jsonLd={[
          buildProductSchema(typeof window !== "undefined" ? window.location.origin : "", product, language),
          buildBreadcrumbSchema(typeof window !== "undefined" ? window.location.origin : "", [
            { name: "Home", path: "/" },
            { name: t.nav.shop, path: "/shop" },
            { name: name, path: `/product/${product.slug}` },
          ]),
        ]}
      />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
        <nav className="text-[10px] tracking-[0.2em] uppercase text-[var(--grey)] mb-8">
          <Link to="/" className="link-lux">Home</Link> / <Link to="/shop" className="link-lux">{t.nav.shop}</Link> / <span className="text-[var(--noir)]">{name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="space-y-3" data-testid="product-gallery">
            <div className="aspect-[4/5] bg-[var(--ivory-deep)] overflow-hidden img-zoom relative">
              <img src={product.images[activeImg]} alt={name} className="w-full h-full object-cover" />
              {oos && (
                <span className="absolute top-5 left-5 bg-[var(--noir)] text-[var(--gold)] text-[11px] tracking-[0.25em] uppercase px-3 py-1.5 font-medium">
                  {t.comingSoon}
                </span>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 lg:gap-3">
                {product.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square bg-[var(--ivory-deep)] overflow-hidden transition-all ${activeImg === i ? "ring-2 ring-[var(--gold)]" : "opacity-75 hover:opacity-100"}`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--gold-deep)] mb-3">LUXERA KaLiMa</div>
            <h1 className="font-display text-3xl lg:text-5xl leading-[1.05] mb-4" data-testid="product-name">{name}</h1>
            {tagline && <p className="font-serif text-xl text-[var(--noir-soft)] italic mb-5">— {tagline}</p>}

            {product.reviews_count > 0 && (
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[var(--gold)]">{"★".repeat(5)}</span>
                <span className="text-xs text-[var(--grey)]">{product.rating.toFixed(1)} · {product.reviews_count} {isDE ? "Bewertungen" : "reviews"}</span>
              </div>
            )}

            <div className="flex items-baseline gap-3 mb-2" data-testid="product-price">
              <span className="font-serif text-3xl">{formatPrice(product.price, currency)}</span>
              {onSale && <span className="text-base text-[var(--grey)] line-through">{formatPrice(product.compare_at_price, currency)}</span>}
              {onSale && <span className="bg-[var(--gold)] text-[var(--noir)] text-[10px] px-2 py-0.5 tracking-[0.15em] uppercase">Save {Math.round((1 - product.price / product.compare_at_price) * 100)}%</span>}
            </div>
            <p className="text-xs text-[var(--grey)] mb-7">{isDE ? "Inkl. MwSt. Kostenloser Versand in Deutschland." : "Tax included. Free shipping in Germany."}</p>

            <p className="text-[15px] leading-relaxed text-[var(--noir-soft)] mb-7 whitespace-pre-line" data-testid="product-description" dangerouslySetInnerHTML={{ __html: (desc || "").replace(/\*\*([^*]+)\*\*/g, '<strong class="text-[var(--noir)] font-medium">$1</strong>') }} />

            {/* Bullets */}
            {bullets && bullets.length > 0 && (
              <ul className="space-y-2 mb-7">
                {bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm text-[var(--noir-soft)]">
                    <Sparkles className="w-4 h-4 text-[var(--gold-deep)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap gap-2 mb-7">
              {badges && badges.map((b) => (
                <span key={b} className="text-[10px] tracking-[0.2em] uppercase border border-[var(--champagne-deep)] text-[var(--noir)] px-3 py-1.5 bg-white">{b}</span>
              ))}
            </div>

            {!oos && (
              <div className="flex items-center gap-2 text-xs text-[var(--gold-deep)] mb-6">
                <AlertCircle className="w-4 h-4" />
                <span className="tracking-wide">{t.product.limitedStock}</span>
              </div>
            )}

            {oos ? (
              <div className="bg-[var(--ivory-deep)] p-6 mb-4" data-testid="oos-notify-block">
                <div className="text-[11px] tracking-[0.25em] uppercase text-[var(--gold-deep)] mb-2">{t.comingSoon}</div>
                <p className="text-sm text-[var(--noir-soft)] mb-4">{t.notifyMe}</p>
                <form onSubmit={notifyRestock} className="flex gap-2">
                  <input
                    type="email"
                    required
                    placeholder={t.notifyEmail}
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="flex-1 border border-[var(--champagne-deep)] px-4 py-3 text-sm bg-white outline-none focus:border-[var(--noir)]"
                    data-testid="notify-email"
                  />
                  <button className="bg-[var(--noir)] text-[var(--ivory)] px-5 py-3 text-[11px] tracking-[0.25em] uppercase hover:text-[var(--gold)]" data-testid="notify-submit">
                    {t.notifySubmit}
                  </button>
                </form>
                {notifySent && <p className="text-[var(--gold-deep)] text-sm mt-3" data-testid="notify-success">{t.notifySuccess}</p>}
              </div>
            ) : (
              <div className="flex gap-3 mb-4">
                <div className="flex items-center border border-[var(--noir)]">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3.5" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
                  <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-3 py-3.5" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
                </div>
                <button
                  onClick={() => addToCart(product.id, qty)}
                  className="btn-lux flex-1 py-4 text-[11px] tracking-[0.3em] uppercase bg-[var(--noir)] text-[var(--ivory)] hover:text-[var(--gold)]"
                  data-testid="add-to-cart-btn"
                >
                  {t.product.addToCart}
                </button>
                <button
                  onClick={() => toggleWishlist(product.id)}
                  className="border border-[var(--noir)] w-14 flex items-center justify-center hover:bg-[var(--noir)] hover:text-[var(--ivory)] transition-colors"
                  data-testid="wishlist-toggle"
                >
                  <Heart className={`w-5 h-5 ${inWish ? "fill-current" : ""}`} />
                </button>
              </div>
            )}

            <div className="text-[10px] text-[var(--grey)] tracking-[0.2em] uppercase mb-8">
              {isDE ? "Bestellanfrage — wir kontaktieren Sie zur Bestätigung" : "Order request — we'll contact you to confirm"}
            </div>

            <div className="divide-y divide-[var(--champagne)]/60 border-y border-[var(--champagne)]/60">
              {[
                { id: "details", title: isDE ? "Spezifikationen" : t.product.details, content: (
                  <ul className="space-y-2 text-sm text-[var(--noir-soft)] py-4">
                    {product.specs && product.specs.map((d, i) => <li key={i} className="flex gap-2"><span className="text-[var(--gold)] flex-shrink-0">·</span>{d}</li>)}
                  </ul>
                )},
                { id: "shipping", title: t.product.shipping, content: <p className="py-4 text-sm leading-relaxed text-[var(--noir-soft)]">{t.product.shippingBody}</p> },
              ].map(({ id, title, content }) => (
                <div key={id}>
                  <button onClick={() => setOpenAccordion(openAccordion === id ? null : id)} className="w-full py-4 flex items-center justify-between text-[12px] tracking-[0.2em] uppercase" data-testid={`acc-${id}`}>
                    {title}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === id ? "rotate-180" : ""}`} />
                  </button>
                  {openAccordion === id && content}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-16 mt-16 border-y border-[var(--champagne)]/40">
          {[
            { Icon: Truck, label: isDE ? "Kostenloser Versand DE" : "Free Shipping in DE" },
            { Icon: RefreshCw, label: isDE ? "30 Tage Rückgabe" : "30-Day Returns" },
            { Icon: Shield, label: isDE ? "180 Tage Garantie" : "180-Day Warranty" },
            { Icon: Heart, label: isDE ? "Hypoallergen" : "Hypoallergenic" },
          ].map(({ Icon, label }, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <Icon className="w-7 h-7 text-[var(--gold-deep)] mb-2" strokeWidth={1.25} />
              <div className="text-[10px] tracking-[0.25em] uppercase">{label}</div>
            </div>
          ))}
        </div>

        {/* Reviews */}
        {reviews.length > 0 && (
          <section className="mt-20" data-testid="reviews-list">
            <h2 className="font-display text-3xl lg:text-4xl mb-10">{t.product.reviews}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white p-7 border border-[var(--champagne)]/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[var(--gold)] text-sm">{"★".repeat(r.rating)}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--grey)]">{t.product.verified}</div>
                  </div>
                  <h3 className="font-serif text-lg mb-2">{r.title}</h3>
                  <p className="text-sm text-[var(--noir-soft)] leading-relaxed mb-3">{r.body}</p>
                  <div className="text-xs text-[var(--grey)]">— {r.author}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="mt-20 max-w-3xl mx-auto" data-testid="product-faq">
          <h2 className="font-display text-3xl lg:text-4xl text-center mb-10">{t.product.faq}</h2>
          <div className="divide-y divide-[var(--champagne)]/60 border-y border-[var(--champagne)]/60">
            {faqs.map((f, i) => (
              <div key={i}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full py-5 flex items-center justify-between text-left">
                  <span className="font-serif text-lg">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="pb-5 text-sm text-[var(--noir-soft)] leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-24" data-testid="complete-look">
            <h2 className="font-display text-3xl lg:text-4xl text-center mb-10">{t.product.complete}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {showSticky && !oos && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[var(--champagne)] py-3 px-4 z-40 shadow-2xl" data-testid="sticky-add-cart">
          <div className="max-w-[1500px] mx-auto flex items-center gap-4">
            <img src={product.images[0]} alt="" className="w-12 h-14 object-cover hidden sm:block" />
            <div className="flex-1 min-w-0">
              <div className="font-serif text-sm truncate">{name}</div>
              <div className="text-xs text-[var(--grey)]">{formatPrice(product.price, currency)}</div>
            </div>
            <button onClick={() => addToCart(product.id, qty)} className="bg-[var(--noir)] text-[var(--ivory)] px-6 py-3 text-[11px] tracking-[0.25em] uppercase hover:text-[var(--gold)]">
              {t.product.addToCart}
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductPage;
